import { TableRowSelection } from 'antd/lib/table/interface';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from '../../../tables/WorkerTable/WorkerTable.styles';
import { EditableCell } from '@app/components/tables/editableTable/EditableCell';
import { FilterIcon } from '../../icons/FilterIcon';
import { dealRoomDisabledSections } from '@app/constants/dealRoom';
import { IDealRoomMenuSection, IDealRoomSection, IDealRoomSectionContent } from '@app/interfaces/deal-room';
import { disabledMenuColumns } from './Column';
import { Button } from '../../buttons/Button/Button';
import { AddDealRoomMenu } from '../AddDealRoomMenu/AddDealRoomMenu';
import { notificationController } from '@app/controllers/notificationController';
import { deleteDealRoomMenu, getDealRoomMenuByDealRoomId } from '@app/api/deal-room-menu.api';
import { IDealRoomMenu } from '@app/interfaces/deal-room-menu';
import { BasicTableRow } from '@app/api/table.api';
import { Select, Space } from 'antd';
import { Popconfirm } from '../../Popconfirm/Popconfirm';
import { EditIcon } from '../../icons/EditIcon';
import { DeleteIcon } from '../../icons/DeleteIcon';
import { getCategoryOptions } from '@app/api/deal-room-category';
import { IDealRoomCategory } from '@app/interfaces/deal-room-category';

export interface DisabledMenuProps {
  dealRoomId: string;
  sections: IDealRoomSection[];
  menuSections: IDealRoomMenuSection[];
  addCount: number;
  activeTab: string;
  onSelect: (file: IDealRoomSectionContent[]) => void;
  onRemoveMenu: (menu: string) => void;
}

const buttons = [`מנורה`, `פניקס`, `כלל`, `הכשרה`, `איילון`];

export const DisabledMenu: React.FC<DisabledMenuProps> = ({
  dealRoomId,
  sections,
  menuSections,
  addCount,
  activeTab,
  onSelect,
  onRemoveMenu,
}) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loader, setLoader] = useState(false);
  const [menus, setMenus] = useState<IDealRoomMenu[]>([]);
  const [categories, setCategories] = useState<IDealRoomCategory[]>([]);
  const [selected, setSelected] = useState<IDealRoomSectionContent[]>([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<IDealRoomMenu | null>(null);
  const [categorySelections, setCategorySelections] = useState<Record<string, string>>({});
  const categorySelectionsRef = useRef(categorySelections);

  const fetchDealRoomCategories = useCallback(async () => {
    setLoader(true);
    try {
      const categories = await getCategoryOptions();
      setCategories(categories);
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  }, [setCategories]);

  const fetchDealRoomMenus = useCallback(async () => {
    setLoader(true);
    try {
      const menus = await getDealRoomMenuByDealRoomId(dealRoomId);
      setMenus(menus);
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  }, [dealRoomId]);

  useEffect(() => {
    categorySelectionsRef.current = categorySelections;
  }, [categorySelections]);

  const [query, setQuery] = useState('');
  const [tableData, setTableData] = useState<IDealRoomSectionContent[]>([]);

  const onAddRoomMenuSuccess = () => {
    fetchDealRoomMenus();
    setOpenMenu(false);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.toLowerCase();
    setQuery(value);
  };

  const onDelete = async (id: string) => {
    setLoader(true);
    try {
      onRemoveMenu(id);
      await deleteDealRoomMenu(id);
      notificationController.success({ message: t('dealRooms.selectMenu.deleteSuccess') });
      fetchDealRoomMenus();
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoader(false);
    }
  };

  const columns = [
    ...disabledMenuColumns?.map((item) => {
      item.title = t(item.title);
      return item;
    }),
    {
      title: t('dealRooms.selectMenu.category'),
      dataIndex: 'category',
      width: 200,
      render: (_: unknown, record: IDealRoomSectionContent) => {
        return (
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder={t('dealRooms.selectMenu.selectCategory')}
            value={categorySelections[record.key as string] ?? undefined}
            onChange={(value: string | undefined) =>
              setCategorySelections((prev) => {
                const next = { ...prev };
                if (value === undefined) {
                  delete next[record.key as string];
                } else {
                  next[record.key as string] = value;
                }
                return next;
              })
            }
            options={categories.map((c) => ({ label: c.name, value: c._id }))}
          />
        );
      },
    },
    {
      title: t('dealRooms.selectMenu.actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center' as const,
      render: (text: string, record: BasicTableRow) => {
        return (
          <>
            {record?.dealRoomMenu && (
              <Space>
                <S.ActionButton
                  title={t('common.edit')}
                  onClick={() => {
                    setSelectedMenu((record?.dealRoomMenu as IDealRoomMenu) || null);
                    setOpenMenu(true);
                  }}
                >
                  <EditIcon />
                </S.ActionButton>
                <Popconfirm
                  title={t('dealRooms.selectMenu.deleteInfo')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => onDelete((record?.dealRoomMenu as IDealRoomMenu)?.id || '')}
                >
                  <S.ActionButton title={t('common.delete')}>
                    <DeleteIcon />
                  </S.ActionButton>
                </Popconfirm>
              </Space>
            )}
          </>
        );
      },
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: IDealRoomSectionContent[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelected(selectedRows);
  };

  const rowSelection: TableRowSelection<IDealRoomSectionContent> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const updateSearchQuery = (value: string) => {
    setQuery(value);
  };

  useEffect(() => {
    fetchDealRoomMenus();
  }, [fetchDealRoomMenus]);

  useEffect(() => {
    getCategoryOptions()
      .then(setCategories)
      .catch((error) => notificationController.error({ message: (error as Error).message }));
  }, []);

  useEffect(() => {
    if (activeTab) {
      fetchDealRoomCategories();
    }
  }, [fetchDealRoomCategories, activeTab]);

  const selectedRef = useRef(selected);
  const onSelectRef = useRef(onSelect);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    if (addCount > 0) {
      const withCategories = selectedRef.current.map((item) => ({
        ...item,
        category: categorySelectionsRef.current[item.key as string] ?? null,
      }));
      onSelectRef.current(withCategories);
    }
  }, [addCount]);

  useEffect(() => {
    const tableData = dealRoomDisabledSections
      .filter((item) => !sections.find((section) => section.section === item.key))
      .map((item) => {
        item.label = t(item.labelKey || '');
        return item;
      });
    const menuData = menus
      .map((menu) => ({
        key: menu.id,
        label: menu.name,
        labelKey: menu.name,
        default: false,
        display: false,
        highlighted: false,
        isEditing: false,
        dealRoomMenu: menu,
      }))
      .filter((item) => !menuSections.find((section) => (section.menu as IDealRoomMenu).id === item.key));

    // Ensure all items in the array conform to IDealRoomSectionContent by adding missing required properties
    setTableData(
      [...tableData, ...menuData]
        .map((item) => ({
          ...item,
          isEditing: item?.isEditing ?? false,
        }))
        .filter((item) =>
          query
            ? item.key.toLowerCase().includes(query) ||
              item.labelKey.toLowerCase().includes(query) ||
              t(item.labelKey || '')
                .toLowerCase()
                .includes(query)
            : true,
        ),
    );
  }, [menus, query, sections, menuSections, t]);

  return (
    <>
      {openMenu && (
        <AddDealRoomMenu
          open={openMenu}
          dealRoomMenu={selectedMenu}
          dealRoomId={dealRoomId}
          onCancel={() => setOpenMenu(false)}
          onSuccess={onAddRoomMenuSuccess}
        />
      )}
      <S.TableActionWrapper className="width-100 between">
        <S.InputSearch
          placeholder={t('header.search')}
          className="search-input"
          onChange={(event) => handleSearch(event)}
          value={query}
          filter={!query ? <S.Btn size="small" type={'text'} aria-label="Filter" icon={<FilterIcon />} /> : null}
          allowClear
          enterButton={null}
          addonAfter={null}
        />
        <S.FlexWrapper>
          <Space.Compact>
            {buttons.map((btn) => (
              <Button
                key={btn}
                className="width-auto font-sm hover-button"
                type="ghost"
                onClick={() => updateSearchQuery(btn)}
              >
                {btn}
              </Button>
            ))}
          </Space.Compact>
          <Button type="primary" onClick={() => setOpenMenu(true)}>
            {t('dealRooms.selectMenu.addSection')}
          </Button>
        </S.FlexWrapper>
      </S.TableActionWrapper>
      <S.ScrollTable
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        bordered
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        dataSource={tableData}
        columns={columns}
        loading={loader}
        rowClassName="editable-row"
        pagination={false}
        locale={{ emptyText: t('common.noData') }}
        // scroll={{ y: 400 }}
      />
    </>
  );
};
